import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Modal, Select, type SelectOption } from '../../components';
import { apiErrorMessage, apiRequest } from '../../lib/api';
import type { ListResponse } from '../../lib/useCrud';
import type { UserOption } from '../leads/types';
import type { FollowupCreatePayload, FollowupLinkType, FollowupRow } from './types';

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ENTITY_OPTIONS: SelectOption[] = [
  { value: 'lead', label: 'Lead' },
  { value: 'client', label: 'Client' },
  { value: 'contact', label: 'Contact' },
];

const ENTITY_PATH: Record<FollowupLinkType, string> = {
  lead: '/leads',
  client: '/clients',
  contact: '/contacts',
};

type EntityOption = { id: string; full_name: string | null };

function fromDatetimeLocal(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** ISO timestamp → the `YYYY-MM-DDTHH:mm` shape a datetime-local input expects (local time). */
function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Shared create/edit modal for follow-ups. When `row` is provided the modal
 * runs in edit mode: the linked entity (lead/client/contact) is read-only so we
 * never risk breaking the "exactly one of lead/client/contact" rule, and the
 * parent maps the emitted payload onto a PATCH that only touches the editable
 * fields. In create mode the linked entity is picked here and sent on the POST.
 */
export function FollowupFormModal({
  row,
  users,
  onClose,
  onSubmit,
}: {
  row?: FollowupRow;
  users: UserOption[];
  onClose: () => void;
  onSubmit: (payload: FollowupCreatePayload) => Promise<void>;
}) {
  const isEdit = Boolean(row);

  const [entityType, setEntityType] = useState<FollowupLinkType>(row?.link?.type ?? 'lead');
  const [entityId, setEntityId] = useState(row?.link?.id ?? '');
  const [entitySearch, setEntitySearch] = useState('');
  const [type, setType] = useState(row?.followup_type ?? '');
  const [description, setDescription] = useState(row?.description ?? '');
  const [status, setStatus] = useState<string>(row?.status ?? 'pending');
  const [due, setDue] = useState(toDatetimeLocal(row?.due_date ?? null));
  const [assignee, setAssignee] = useState(row?.assigned_user_id ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entitiesQ = useQuery({
    queryKey: ['followup-entity-picker', entityType, entitySearch],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '50' });
      if (entitySearch.trim()) params.set('q', entitySearch.trim());
      return apiRequest<ListResponse<EntityOption>>(
        `${ENTITY_PATH[entityType]}?${params.toString()}`,
      );
    },
    // In edit mode the linked entity is read-only, so skip the picker fetch.
    enabled: !isEdit,
  });

  const entityOpts: SelectOption[] = (entitiesQ.data?.data ?? []).map((e) => ({
    value: e.id,
    label: e.full_name ?? 'Unnamed',
  }));

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!isEdit && !entityId) {
      setError(`Pick a ${entityType} to link this follow-up to.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload: FollowupCreatePayload = {
        followup_type: type.trim() || null,
        description: description.trim() || null,
        status,
        due_date: fromDatetimeLocal(due),
        assigned_user_id: assignee || null,
      };
      // Linked entity is only set on create; in edit mode the parent strips
      // these keys before PATCHing, so the existing link is preserved as-is.
      if (!isEdit) payload[`${entityType}_id`] = entityId;
      await onSubmit(payload);
    } catch (err) {
      setError(apiErrorMessage(err, 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const userOpts: SelectOption[] = users.map((u) => ({ value: u.id, label: u.full_name }));

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit follow-up' : 'New follow-up'}
      width={520}
    >
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {isEdit ? (
            <div>
              <label className="field-label">Linked to</label>
              <div className="input" style={{ display: 'flex', alignItems: 'center' }} aria-readonly>
                <span
                  className="badge gray"
                  style={{ marginRight: 6, textTransform: 'capitalize' }}
                >
                  {row?.link?.type ?? '—'}
                </span>
                {row?.link?.label ?? 'Unnamed'}
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 160 }}>
                  <Select
                    id="fu-entity-type"
                    label="Linked to"
                    value={entityType}
                    options={ENTITY_OPTIONS}
                    onChange={(v) => {
                      setEntityType(v as FollowupLinkType);
                      setEntityId('');
                      setEntitySearch('');
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="field-label" htmlFor="fu-entity-search">
                    Find {entityType}
                  </label>
                  <Input
                    icon="search"
                    placeholder={`Search ${entityType}s…`}
                    value={entitySearch}
                    onChange={setEntitySearch}
                  />
                </div>
              </div>
              <Select
                id="fu-entity"
                label={`Select ${entityType}`}
                placeholder={entitiesQ.isLoading ? 'Loading…' : `— choose a ${entityType} —`}
                value={entityId}
                options={entityOpts}
                onChange={setEntityId}
              />
            </>
          )}

          <Input
            id="fu-type"
            label="Type"
            value={type}
            onChange={setType}
            placeholder="call, email, meeting…"
          />
          <div>
            <label className="field-label" htmlFor="fu-desc">
              Description
            </label>
            <textarea
              id="fu-desc"
              className="input"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="field-label" htmlFor="fu-due">
                Due
              </label>
              <input
                id="fu-due"
                className="input"
                type="datetime-local"
                value={due}
                onChange={(e) => setDue(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                id="fu-status"
                label="Status"
                value={status}
                options={STATUS_OPTIONS}
                onChange={setStatus}
              />
            </div>
          </div>
          <Select
            id="fu-assignee"
            label="Assign to"
            placeholder="— unassigned —"
            value={assignee}
            options={userOpts}
            onChange={setAssignee}
          />
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{
            justifyContent: 'flex-end',
            borderTop: '1px solid var(--border)',
            borderBottom: 'none',
          }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
